/**
 * TowerPreview — a living, lit tower "specimen" for the right-HUD armory (BL-55).
 *
 * The deploy column used to show a tiny static `drawIcon` clip-art glyph. This
 * is the armory upgrade: each bay draws the **exact engine render** of the tower
 * node — the real `ELEMENT_REGISTRY[element].drawNode` the arena calls (fire's
 * spike-ring rotating, ICE's hex-cage breathing, EMP's arcs crackling) — onto a
 * small per-bay `<canvas>`, sitting in a lit-CRT bay (`UIMaterial.drawLitPanel`),
 * and routed through the ONE shared `UIBloom` so the hot cores/edges flare
 * exactly like the battlefield (the `SkillCard`/`ProBar` one-shared-instance
 * contract — never a per-bay WebGL context).
 *
 * It is cheap by construction: a small canvas, idle-throttled to ~30 fps, PAUSED
 * when scrolled offscreen (IntersectionObserver), powering-up on hover (faster
 * animation + a brighter bay), and — under `prefers-reduced-motion` — a single
 * static lit frame (no loop, no flare). When no real 2D context is available
 * (headless / jsdom / the BL-44 degrade rung) it stays a silent no-op and the
 * DOM bay's CSS lit edge carries the read.
 *
 * Render-only (Layer 4) → determinism-neutral; the sim never sees it. We *call*
 * the arena `drawNode`/`drawLitPanel`/`UIBloom` — we never modify them.
 *
 * Reusable: `TowerSelector` mounts one per tower bay (and the firewall sentinel,
 * via a green `draw` override since it has no arena `drawNode`); the helper is
 * deliberately generic so `showTowerInfo` and future surfaces can reuse it.
 */
import type { Tower } from "../entities/Tower.ts";
import { displayDpr } from "../utils/displayDpr.ts";
import type { Element } from "../entities/elements.ts";
import { ELEMENT_REGISTRY } from "../rendering/ElementRegistry.ts";
import { roundedRectPath } from "./UIMaterial.ts";
import { PANEL } from "../rendering/designTokens.ts";
import { getSharedUIBloom } from "./ProBar.ts";
import { isReducedMotion, motionScale } from "../engine/MotionSettings.ts";

/** A draw call into the bay's local canvas space (used for the firewall, which
 *  has no arena `drawNode`). For the six elements the default calls `drawNode`. */
export type PreviewDrawFn = (
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
  time: number,
) => void;

export interface TowerPreviewOptions {
  /** Element whose real `drawNode` renders the specimen. Omit + pass `draw` for
   *  the firewall sentinel (no arena `drawNode`). */
  element?: Element;
  /** Base tower colour (a central palette hex — drives the bay edge + node). */
  color: string;
  /** Light/accent colour (rings, cores). */
  colorLight: string;
  /** Current tier (1..5) — the node draws richer at higher tiers (tier-aware). */
  tier?: number;
  /** CSS box size in px (square). Default 46. */
  size?: number;
  /** Custom draw (firewall) — overrides the element `drawNode`. */
  draw?: PreviewDrawFn;
}

/** ~30 fps idle cap — the §6.8 living-node tempo at a fraction of the cost. */
const IDLE_FRAME_MS = 1000 / 30;
/** Hover power-up paints smoother (the bay is the focus). */
const HOVER_FRAME_MS = 1000 / 60;
/** Animation-clock speeds (fraction of arena real-time) — deliberately CALM:
 *  a slow, edel breathing at rest, a gentle lift on hover (the owner wants the
 *  specimen elegant, not frantic). */
const IDLE_SPEED = 0.4;
const HOVER_SPEED = 0.72;

export class TowerPreview {
  /** The bay canvas — appended into a `.tower-btn` (or reused elsewhere). */
  readonly el: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D | null;
  private readonly mock: Tower;
  private readonly drawFn: PreviewDrawFn;
  private boxSize: number;
  /** A genuine 2D context (exposes the gradient builders the engine material
   *  needs). Headless / jsdom stubs report false → we stay a silent no-op. */
  private readonly real: boolean;
  private color: string;
  private hovered = false;
  private animTime = 0;
  private lastTs = 0;
  private accum = 0;
  private raf = 0;
  private visible = true;
  private destroyed = false;
  private io: IntersectionObserver | null = null;

  constructor(opts: TowerPreviewOptions) {
    this.boxSize = opts.size ?? 46;
    this.color = opts.color;
    const tier = clampTier(opts.tier ?? 1);

    this.el = document.createElement("canvas");
    this.el.className = "tower-preview-canvas";
    this.el.setAttribute("aria-hidden", "true");
    this.el.style.width = `${this.boxSize}px`;
    this.el.style.height = `${this.boxSize}px`;
    this.ctx = this.el.getContext("2d");
    this.real =
      !!this.ctx &&
      typeof this.ctx.createRadialGradient === "function" &&
      typeof this.ctx.createLinearGradient === "function";

    const c = this.boxSize / 2;
    // The mock tower — the minimal shape the arena `drawNode` reads (element /
    // level / colours + the kinetic barrel fields). Never enters the sim.
    this.mock = {
      element: opts.element ?? "water",
      level: tier,
      x: c,
      y: c,
      color: opts.color,
      colorLight: opts.colorLight,
      barrelAngle: -Math.PI / 2,
      sniperState: "idle",
    } as unknown as Tower;

    this.drawFn =
      opts.draw ??
      ((ctx, x, y, size, time): void => {
        ELEMENT_REGISTRY[this.mock.element].drawNode(
          ctx,
          x,
          y,
          size,
          this.mock,
          time,
        );
      });
  }

  /** Paint a first frame and — in motion — start the idle-throttled loop. */
  mount(): void {
    this.paint();
    if (isReducedMotion() || !this.real) return; // a static lit specimen
    this.observe();
    this.start();
  }

  /** Tier-aware: re-tier the specimen (e.g. preview the next upgrade). */
  setTier(tier: number): void {
    const t = clampTier(tier);
    if (this.mock.level === t) return;
    this.mock.level = t;
    if (isReducedMotion() || !this.real || !this.raf) this.paint();
  }

  /** Hover power-up: faster animation + a brighter bay. Resumes the loop if a
   *  reduced-motion bay was sitting static. */
  setHover(on: boolean): void {
    if (this.hovered === on) return;
    this.hovered = on;
    if (isReducedMotion()) {
      this.paint();
      return;
    }
    if (on && this.real && !this.raf && this.visible) this.start();
    else if (!this.raf) this.paint();
  }

  private observe(): void {
    if (typeof IntersectionObserver === "undefined") return;
    this.io = new IntersectionObserver((entries) => {
      const e = entries[entries.length - 1];
      this.visible = !!e?.isIntersecting;
      if (this.visible) this.start();
      else this.stop();
    });
    this.io.observe(this.el);
  }

  private start(): void {
    if (this.raf || this.destroyed || !this.real) return;
    this.lastTs = now();
    const tick = (): void => {
      if (this.destroyed || !this.visible) {
        this.raf = 0;
        return;
      }
      const ts = now();
      const dt = ts - this.lastTs;
      this.lastTs = ts;
      // Hover lifts the animation clock a touch; idle breathes slowly. Both
      // honour the global motion scale (reduced motion stops the clock).
      const speed = this.hovered ? HOVER_SPEED : IDLE_SPEED;
      this.animTime += (dt / 1000) * speed * motionScale();
      this.accum += dt;
      const frameMs = this.hovered ? HOVER_FRAME_MS : IDLE_FRAME_MS;
      if (this.accum >= frameMs) {
        this.accum = 0;
        this.paint();
      }
      this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }

  /**
   * BL-290 — resize the specimen box. The bay's size is derived from the LIVE
   * right-rail width (see `TowerSelector.bayPreviewPx`), and that width changes
   * with the device aspect — including on an orientation flip — so the size
   * cannot be frozen at construction. `paint()` reads `boxSize` every frame, so
   * updating it here is enough; the backing store follows on the next frame.
   */
  setSize(px: number): void {
    const next = Math.max(1, Math.round(px));
    if (next === this.boxSize) return;
    this.boxSize = next;
    this.el.style.width = `${next}px`;
    this.el.style.height = `${next}px`;
  }

  private stop(): void {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
  }

  /** Draw the lit bay + the real node specimen, then route the hot canvas
   *  through the ONE shared UI bloom (the ProBar / SkillCard pattern). */
  paint(): void {
    const ctx = this.ctx;
    if (!ctx || !this.real || this.destroyed) return;
    const dpr = Math.max(
      1,
      displayDpr(),
    );
    const box = this.boxSize;
    this.el.width = Math.round(box * dpr);
    this.el.height = Math.round(box * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, box, box);

    // The elegant black-glass bay (NOT a bright lit panel) — a near-black
    // surface + a thin dim element edge; the specimen alone carries the light.
    this.drawBay(ctx, box);

    // The specimen: the exact engine render on the per-bay canvas (sized to
    // leave the node's soft glow room inside the bay glass).
    const c = box / 2;
    this.mock.x = c;
    this.mock.y = c;
    const nodeSize = box * (this.hovered ? 0.7 : 0.66);
    ctx.save();
    this.drawFn(ctx, c, c, nodeSize, this.animTime);
    ctx.restore();

    // Gentle shared WebGL bloom — only the node's hot cores flare; the dim bay
    // edge stays under the UI bloom threshold, so the bay never blooms into a
    // bright box. Null in headless / no-WebGL2.
    const bloom = getSharedUIBloom();
    if (bloom) {
      bloom.setDpr(dpr);
      const out = bloom.process(this.el);
      if (out) {
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.globalAlpha = this.hovered ? 0.6 : 0.45;
        ctx.drawImage(out, 0, 0, this.el.width, this.el.height);
        ctx.globalAlpha = 1;
      }
    }
  }

  /** The elegant black-glass bay: a near-black surface (the central `PANEL.BG`
   *  → pure black) clipped to the rounded rect, with a single thin, dim
   *  element-tinted edge for identity. No emissive fill, no glowCache seeds —
   *  the bay reads dark and premium; only the living node glows. */
  private drawBay(ctx: CanvasRenderingContext2D, box: number): void {
    roundedRectPath(ctx, 1, 1, box - 2, box - 2, 6);
    ctx.save();
    ctx.clip();
    const g = ctx.createLinearGradient(0, 0, 0, box);
    g.addColorStop(0, PANEL.BG);
    g.addColorStop(1, "#000000");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, box, box);
    ctx.restore();

    roundedRectPath(ctx, 1, 1, box - 2, box - 2, 6);
    ctx.lineWidth = 1;
    ctx.strokeStyle = this.color;
    ctx.globalAlpha = this.hovered ? 0.4 : 0.2;
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  destroy(): void {
    this.destroyed = true;
    this.stop();
    this.io?.disconnect();
    this.io = null;
    this.el.remove();
  }
}

function clampTier(t: number): number {
  return Math.max(1, Math.min(5, Math.round(t) || 1));
}

function now(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}
